import { FormProvider, getFormProps } from '@conform-to/react'
import { Button } from '@radix-ui/themes'
import * as React from 'react'
import { RxTrash } from 'react-icons/rx'
import { z } from 'zod'
import { FormDefaults } from '#app/components/common/form-defaults.tsx'
import { SubmissionButton } from '#app/components/common/submission-button.tsx'
import { type GetAddress } from '#app/models/ecommerce-providers/sylius/shop/fetch/address/get-address.ts'
import { type Locale } from '#app/models/locale.ts'
import { type FormEnum } from '#app/routes/_layout/route-config.ts'
import { generateItemFormFetcherKey } from '#app/utils/client/generate-item-form-fetcher-key.ts'
import { generateItemFormId } from '#app/utils/client/generate-item-form-id.ts'
import { schemaDefault } from '#app/utils/form-defaults.ts'
import { useRemoveAddressForm } from '#app/utils/hooks/forms/use-remove-address-form.ts'

/* -------------------------------------------------------------------------------------------------
 * Remove address form
 * -----------------------------------------------------------------------------------------------*/

enum INPUTS {
	removeAddress = 'remove-address',
}

const FORM: FormEnum = {
	generateId: ({ id }) => generateItemFormId('remove-address-form', id),
	action: '/profile/addresses',
	generateFetcherKey: ({ id }) =>
		generateItemFormFetcherKey('remove-address-form', id),
	subaction: 'remove-address-form',
	redirect: '/profile/addresses',
} as const

const schema = z.object({
	...schemaDefault,
	[INPUTS.removeAddress]: z.number(),
})

type RemoveAddressFormProps = {
	id: GetAddress['id']
	setOpen: any
	locale: Locale
}

const RemoveAddressForm: React.FC<RemoveAddressFormProps> = ({
	id,
	setOpen,
	locale,
}) => {
	const {
		removeAddressFetcher,
		removeAddressFormValues,
		removeAddressForm,
		removeAddressFields,
		t,
	} = useRemoveAddressForm({ id })

	return (
		<FormProvider context={removeAddressForm.context}>
			<removeAddressFetcher.Form
				method="post"
				action={`/${locale}${FORM.action}${location.search}`}
				{...getFormProps(removeAddressForm)}
				onSubmit={() => setOpen(false)}
				preventScrollReset
			>
				<FormDefaults
					form={removeAddressForm}
					fields={removeAddressFields}
					values={removeAddressFormValues}
				/>
				<Button size="3" color="red" type="submit" variant="soft">
					<SubmissionButton fetcher={removeAddressFetcher}>
						<RxTrash size="1.2em" />
						{t('items.button.removeAddressSubmit')}
					</SubmissionButton>
				</Button>
			</removeAddressFetcher.Form>
		</FormProvider>
	)
}
RemoveAddressForm.displayName = 'RemoveAddressForm'

/* -----------------------------------------------------------------------------------------------*/

export {
	type RemoveAddressFormProps,
	RemoveAddressForm,
	FORM as REMOVE_ADDRESS_FORM,
	schema as removeAddressSchema,
	INPUTS as REMOVE_ADDRESS_INPUTS,
}
